/**
 * The camera button, and the toast that appears after the shutter.
 *
 * One button, bottom-right, above the control hint strip — the last quadrant of the screen with
 * nothing in it (the perf overlay owns top-left, the flight HUD top-right, the environment panel
 * bottom-left, the status pill bottom-centre).
 *
 * ## Why a button at all, when there is a key
 *
 * Because on a phone there is no key, and because a keyboard shortcut nobody knows about is a
 * feature nobody uses. The shortcut is `6`, which sits directly after the five camera keys `1`–`5`
 * — cameras one to five choose the view, and six keeps it. That adjacency is the whole mnemonic,
 * and it is why the binding is a digit rather than one of the function keys: `F3` is already the
 * perf overlay and on macOS that key is Mission Control, `F9` is a media key, and a screenshot
 * button that skips the pilot's music is not a good screenshot button.
 *
 * ## The toast is where the rest of the share loop lives
 *
 * The shutter itself either opens a share sheet (phones, and desktop Safari) or downloads a PNG.
 * Neither of those gets the *words* onto a post, so the toast carries a "copy share text" action
 * with a pre-written line, and — on the download path — a "save again" for a sheet that was
 * dismissed. It dismisses itself after a few seconds, or on the next shutter.
 *
 * Same cost discipline as the other overlays: the DOM is built once in the constructor, and a
 * frame with no toast up costs one early return.
 *
 * `updateFrame(alpha)`: yes — called from the loop's after-frame slot purely to time the toast
 * out. It returns immediately whenever the toast is hidden, which is almost always.
 */

import type { ShareOutcome } from '../game/screenshot.js';

/** How long the toast stays up before fading [ms]. */
const TOAST_MS = 6_000;

/**
 * The camera glyph, as an inline SVG path.
 *
 * Inline rather than an icon font or an image: it is forty bytes of path data, it inherits
 * `currentColor` so the hover and disabled states are one CSS rule each, and it cannot fail to
 * load — an icon that 404s on a bad deploy leaves a button with nothing on it.
 */
const CAMERA_SVG =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
  '<path d="M4 7.5h3.2l1.4-2.1h6.8l1.4 2.1H20a1.5 1.5 0 0 1 1.5 1.5v8.5A1.5 1.5 0 0 1 20 19H4a1.5 1.5 0 0 1-1.5-1.5V9A1.5 1.5 0 0 1 4 7.5Z"/>' +
  '<circle cx="12" cy="13" r="3.6"/>' +
  '</svg>';

export interface ShareButtonOptions {
  /** Take the picture. Returns false when a capture is already in flight. */
  onCapture: () => boolean;
  /** The pre-written post line, read when "copy share text" is pressed. */
  shareText: () => string;
  /** Put the last shot back into the share sheet. */
  onReshare: () => void;
  /** Save the last shot again. */
  onSave: () => boolean;
  parent?: HTMLElement;
}

export class ShareButton {
  readonly element: HTMLButtonElement;
  readonly toast: HTMLDivElement;

  private readonly options: ShareButtonOptions;
  private readonly toastMessage: HTMLSpanElement;
  private readonly copyButton: HTMLButtonElement;
  private readonly againButton: HTMLButtonElement;
  private toastUntilMs = 0;
  private visibleValue = true;
  /** What the "again" button currently offers. */
  private againAction: 'share' | 'save' = 'save';

  constructor(options: ShareButtonOptions) {
    this.options = options;
    const parent = options.parent ?? document.body;

    this.element = document.createElement('button');
    this.element.type = 'button';
    this.element.className = 'shot';
    this.element.title = 'Screenshot and share (6)';
    this.element.setAttribute('aria-label', 'Take a screenshot and share it');
    this.element.innerHTML = CAMERA_SVG;
    this.element.addEventListener('click', () => {
      // The button is *not* what captures — see `game/screenshot.ts` on why the readback has to
      // wait for the frame loop. All this does is ask.
      if (this.options.onCapture()) this.pressed();
    });

    this.toast = document.createElement('div');
    this.toast.className = 'shot-toast';
    this.toast.hidden = true;
    this.toast.setAttribute('role', 'status');

    this.toastMessage = document.createElement('span');
    this.toastMessage.className = 'shot-toast__message';

    this.copyButton = document.createElement('button');
    this.copyButton.type = 'button';
    this.copyButton.className = 'shot-toast__action';
    this.copyButton.textContent = 'COPY SHARE TEXT';
    this.copyButton.addEventListener('click', () => void this.copy());

    /*
     * One "again" button, whose label and action follow the last outcome.
     *
     * A dismissed share sheet wants the sheet back; a download wants the file again. Two buttons
     * would mean showing one greyed out most of the time, which is more chrome for no more
     * capability — and the two are never both the right offer at once.
     */
    this.againButton = document.createElement('button');
    this.againButton.type = 'button';
    this.againButton.className = 'shot-toast__action';
    this.againButton.addEventListener('click', () => {
      if (this.againAction === 'share') {
        this.options.onReshare();
        return;
      }
      if (this.options.onSave()) this.say('saved again', { again: true, action: 'save' });
    });

    this.toast.append(this.toastMessage, this.copyButton, this.againButton);
    parent.append(this.element, this.toast);
  }

  get visible(): boolean {
    return this.visibleValue;
  }

  /**
   * Hidden while a menu is up, and for the whole of a scripted run — see `main.ts`.
   *
   * Idempotent, because the caller drives it from the after-frame slot: a menu opening is an
   * event nobody publishes, so the cheapest correct thing is to assert the state every frame and
   * make the no-op case a single boolean comparison.
   */
  setVisible(visible: boolean): void {
    if (visible === this.visibleValue) return;
    this.visibleValue = visible;
    this.element.hidden = !visible;
    if (!visible) this.hideToast();
  }

  /**
   * Report what happened to the shot.
   *
   * The wording differs per outcome on purpose. "Saved" and "shared" are different events and a
   * pilot who meant to share should be told plainly that the file went to their downloads instead,
   * because otherwise they will go looking for it in the wrong place.
   */
  report(outcome: ShareOutcome, detail: string): void {
    switch (outcome) {
      case 'shared':
        this.say('shared', { again: false });
        break;
      case 'cancelled':
        this.say('share dismissed', { again: true, action: 'share' });
        break;
      case 'downloaded':
        this.say(detail === '' ? 'saved' : `saved ${detail}`, { again: true, action: 'save' });
        break;
      case 'failed':
        this.say(`screenshot failed — ${detail}`, { again: false, copy: false });
        break;
    }
  }

  /** Time the toast out. Called from the after-frame slot; returns at once when nothing is up. */
  updateFrame(nowMs: number): void {
    if (this.toast.hidden) return;
    if (nowMs < this.toastUntilMs) return;
    this.hideToast();
  }

  private async copy(): Promise<void> {
    const text = this.options.shareText();
    try {
      await navigator.clipboard.writeText(text);
      this.say('post line copied', {
        again: !this.againButton.hidden,
        copy: false,
        action: this.againAction,
      });
    } catch {
      /*
       * The clipboard refuses outside a secure context, and in some browsers without a permission.
       * Rather than a dead end, select the text so ⌘C works: the button becomes a way to *get at*
       * the line rather than a way to fail at copying it.
       */
      this.toastMessage.textContent = text;
      this.toastMessage.classList.add('shot-toast__message--selectable');
      this.toastUntilMs = performance.now() + TOAST_MS * 3;
      selectText(this.toastMessage);
    }
  }

  /**
   * Acknowledge a shutter, whether it came from the button or from key `6`.
   *
   * Public so the key binding can call it: the picture arrives a frame later and is delivered a
   * few milliseconds after that, and without an immediate ring the key feels like it did nothing.
   */
  pressed(): void {
    this.element.classList.remove('shot--flash');
    // Reading `offsetWidth` restarts the animation; without it a second shutter inside the
    // animation's duration does nothing at all and the key looks unresponsive.
    void this.element.offsetWidth;
    this.element.classList.add('shot--flash');
  }

  private say(
    message: string,
    options: { again?: boolean; copy?: boolean; action?: 'share' | 'save' } = {},
  ): void {
    this.toastMessage.textContent = message;
    this.toastMessage.classList.remove('shot-toast__message--selectable');
    this.copyButton.hidden = options.copy === false;
    this.againButton.hidden = options.again !== true;
    if (options.action !== undefined) {
      this.againAction = options.action;
      this.againButton.textContent = options.action === 'share' ? 'SHARE AGAIN' : 'SAVE AGAIN';
    }
    this.toast.hidden = false;
    this.toastUntilMs = performance.now() + TOAST_MS;
  }

  private hideToast(): void {
    this.toast.hidden = true;
    this.toastMessage.classList.remove('shot-toast__message--selectable');
  }

  dispose(): void {
    this.element.remove();
    this.toast.remove();
  }
}

/** Put a selection around an element's text, so the platform's copy shortcut can take it. */
function selectText(element: HTMLElement): void {
  const selection = globalThis.getSelection?.();
  if (selection === null || selection === undefined) return;
  const range = document.createRange();
  range.selectNodeContents(element);
  selection.removeAllRanges();
  selection.addRange(range);
}
